import React from 'react';
import { Tab, Table, Icon, Button } from 'semantic-ui-react'
import { Link } from 'react-router-dom';
import ReactToExcel from 'react-html-table-to-excel'

const currentCalendarYear = () => new Date().getFullYear().toString();

const styles = {
  listItem: {
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    marginBottom: '12px',
    padding: '16px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
  },
  eventInfo: {
    flex: 1,
    minWidth: '250px',
  },
  eventTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#333',
    marginBottom: '6px',
  },
  eventMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '15px',
    fontSize: '0.9rem',
    color: '#666',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },
  statusActive: {
    color: '#569556',
    fontWeight: '500',
  },
  statusDeactivated: {
    color: '#d32f2f',
    fontWeight: '500',
  },
  adminButtons: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  tabPane: {
    backgroundColor: '#f5f5f5',
    padding: '20px',
    borderRadius: '0 0 8px 8px',
    minHeight: '400px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#888',
  },
  printSection: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '20px',
  },
};

export const renderPanes = (events, isSignedIn, currentMonth) => {
  const panes = [
    {
      menuItem: { key: 'month', icon: 'calendar', content: currentMonth },
      render: () => (
        <Tab.Pane style={styles.tabPane}>
          {renderCurrentMonthList(currentMonth, events, isSignedIn)}
        </Tab.Pane>
      ),
    },
    {
      menuItem: { key: 'registration', icon: 'clipboard list', content: 'Open Registration' },
      render: () => (
        <Tab.Pane style={styles.tabPane}>
          {renderRegistrationList(events, isSignedIn)}
        </Tab.Pane>
      ),
    },
    {
      menuItem: { key: 'year', icon: 'calendar alternate', content: currentCalendarYear() },
      render: () => (
        <Tab.Pane style={styles.tabPane}>
          {renderCurrentYearList(events, isSignedIn)}
        </Tab.Pane>
      ),
    },
    {
      menuItem: { key: 'all', icon: 'list', content: 'All Games' },
      render: () => (
        <Tab.Pane style={styles.tabPane}>
          {renderFullList(events, isSignedIn)}
        </Tab.Pane>
      ),
    },
    {
      menuItem: { key: 'export', icon: 'file excel', content: 'Export' },
      render: () => (
        <Tab.Pane style={styles.tabPane}>
          {renderPrintList(events)}
        </Tab.Pane>
      ),
    },
  ];
  return <Tab menu={{ secondary: true, pointing: true }} panes={panes} />;
};

export const shortenUrl = (url) => {
  if (url.length > 30) {
    return url.substring(0, 30) + '...';
  }
  return url;
};

const renderAdmin = (event, isSignedIn) => {
  if (isSignedIn) {
    return (
      <div style={styles.adminButtons}>
        <Button
          as={Link}
          to={`/events/results/${event.event_id}/${event.event_date}`}
          size="small"
          basic
          color="green"
          title="Upload Results"
        >
          <Icon name="upload" /> Results
        </Button>
        <Button
          as={Link}
          to={`/events/edit/${event.event_id}/${event.event_date}`}
          size="small"
          basic
          color="green"
          title="Edit or Remove"
        >
          <Icon name="edit" /> Edit
        </Button>
        <Button
          as={Link}
          to={`/events/hands/${event.event_id}/${event.event_date}`}
          size="small"
          basic
          color="green"
          title="Upload Hands"
        >
          <Icon name="file code" /> Hands
        </Button>
      </div>
    );
  }
  return null;
};

const renderStatus = (event) => {
  const isDeactivated = event.event_activation_status === 'deactivated';
  return (
    <span style={isDeactivated ? styles.statusDeactivated : styles.statusActive}>
      <Icon name={isDeactivated ? 'ban' : 'check circle'} />
      {event.event_activation_status}
    </span>
  );
};

const renderListItem = (event, isSignedIn, index) => {
  return (
    <div key={index} style={styles.listItem}>
      <div style={styles.eventInfo}>
        <div style={styles.eventTitle}>{event.event_name}</div>
        <div style={styles.eventMeta}>
          <span style={styles.metaItem}>
            <Icon name="calendar" />
            {event.event_day} {event.event_month} {event.event_year}
          </span>
          <span style={styles.metaItem}>
            <Icon name="map marker alternate" />
            {event.event_location}
          </span>
          <span style={styles.metaItem}>
            <Icon name="users" />
            {event.event_club}
          </span>
          <span style={styles.metaItem}>
            {renderStatus(event)}
          </span>
        </div>
      </div>
      {renderAdmin(event, isSignedIn)}
    </div>
  );
};

const renderEmptyState = (message) => {
  return (
    <div style={styles.emptyState}>
      <Icon name="inbox" size="huge" style={{ marginBottom: '15px', opacity: 0.5 }} />
      <p>{message}</p>
    </div>
  );
};

const renderPrintList = (events) => {
  return (
    <div style={styles.printSection}>
      <div style={{ marginBottom: '20px' }}>
        <ReactToExcel
          table="gamesTable"
          filename="BridgeMateGames"
          sheet="Games"
          buttonText="Export all games to Excel"
          className="ui green button"
        />
      </div>
      <Table celled striped id="gamesTable">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>#</Table.HeaderCell>
            <Table.HeaderCell>Year</Table.HeaderCell>
            <Table.HeaderCell>Month</Table.HeaderCell>
            <Table.HeaderCell>Day</Table.HeaderCell>
            <Table.HeaderCell>Club</Table.HeaderCell>
            <Table.HeaderCell>Name</Table.HeaderCell>
            <Table.HeaderCell>Location</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {events.map((x, index) => (
            <Table.Row key={index}>
              <Table.Cell>{index + 1}</Table.Cell>
              <Table.Cell>{x.event_year}</Table.Cell>
              <Table.Cell>{x.event_month}</Table.Cell>
              <Table.Cell>{x.event_day}</Table.Cell>
              <Table.Cell>{x.event_club}</Table.Cell>
              <Table.Cell>{x.event_name}</Table.Cell>
              <Table.Cell>{x.event_location}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </div>
  );
};

const renderFullList = (events, isSignedIn) => {
  if (!events || events.length === 0) {
    return renderEmptyState('No games found');
  }
  return events.map((event, index) => renderListItem(event, isSignedIn, index));
};

const renderCurrentYearList = (events, isSignedIn) => {
  const filtered = events.filter((x) => x.event_year === currentCalendarYear());
  if (filtered.length === 0) {
    return renderEmptyState(`No games found for ${currentCalendarYear()}`);
  }
  return filtered.map((event, index) => renderListItem(event, isSignedIn, index));
};

const renderRegistrationList = (events, isSignedIn) => {
  const filtered = events.filter((x) => x.event_registration === 'opened');
  if (filtered.length === 0) {
    return renderEmptyState('No games currently open for registration');
  }
  return filtered.map((event, index) => renderListItem(event, isSignedIn, index));
};

const renderCurrentMonthList = (currentMonth, events, isSignedIn) => {
  const filtered = events
    .filter((x) => x.event_year === currentCalendarYear())
    .filter((x) => x.event_month === currentMonth);
  if (filtered.length === 0) {
    return renderEmptyState(`No games found for ${currentMonth}`);
  }
  return filtered.map((event, index) => renderListItem(event, isSignedIn, index));
};
